import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { vault } from "@/lib/vault"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { api, rawFetch } from "@/lib/api"
import { auth } from "@/lib/auth"
import { dashboardStream } from "@/lib/ws"
import { VoiceLoop, VoiceRecorder } from "@/lib/voice"
import { VoiceStage, type VoiceStageState } from "@/components/VoiceStage"
import { VideoCall } from "@/components/VideoCall"
import { AttachmentCard, ConversationDocs } from "@/components/Attachments"
import { Spinner, Toast, Badge } from "@/components/ui"
import { Markdown } from "@/components/Markdown"
import type {
  ChatIn, ChatOut, ChatTurnOut, ConversationOut, MessageOut,
  EmailSummary, DriveFileSummary, CalendarEventSummary,
} from "@/lib/types"
import { MODEL_OPTIONS, getStoredModel, setStoredModel } from "@/lib/models"
import { t, useT } from "@/lib/i18n"
import { SpeechQueue, disableOutputMeter, enableOutputMeter, onSpeakingChange, prefetchFillers, speak, speakingKey, splitSentences, stopSpeaking } from "@/lib/tts"
import { getLocale } from "@/lib/i18n"
import { timeAgo, formatBytes } from "@/lib/utils"
import {
  SendIcon,
  MicIcon,
  StopCircleIcon,
  PlusIcon,
  ChevronDownIcon,
  CheckIcon,
  ArrowUpRightIcon,
  FileIcon,
  CalendarDaysIcon,
  MapPinIcon,
  UsersIcon,
  LockIcon,
  SearchIcon,
  PaperclipIcon,
  XIcon,
  SquareIcon,
  Volume2Icon,
  WandIcon,
  HeadphonesIcon,
  VideoIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
} from "lucide-react"
import type { CallStatusOut, PromptTemplateOut } from "@/lib/types"

export const Route = createFileRoute("/_app/chat")({
  validateSearch: (search: Record<string, unknown>): { c?: string; q?: string } => ({
    c: typeof search.c === "string" && search.c.length > 0 ? search.c : undefined,
    q: typeof search.q === "string" && search.q.length > 0 ? search.q : undefined,
  }),
  component: ChatPage,
})

interface AttChip {
  id: string
  name: string
  size: number
}

interface ImageAtt {
  b64: string
  mediaType: string
  previewUrl: string
  name: string
}

/** Must match ALLOWED_UPLOAD_KINDS in app/api/dashboard.py. */
const DOC_EXT = ["pdf", "docx", "pptx", "xlsx", "txt", "md"]
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"]
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024

interface UIMessage {
  id: string
  role: "user" | "assistant" | "system"
  text: string
  pending?: boolean
  /** Deltas are arriving over SSE; text is partial. */
  streaming?: boolean
  failed?: boolean
  turn_id?: string
  pendingStatus?: "queued" | "running"
  attachments?: AttChip[]
  /** Documents / videos Lucy produced in this turn (attachment ids). */
  output_attachment_ids?: string[]
  image_preview?: string
  feedback?: "up" | "down" | null
  image_backends?: string[]
  provider?: string | null
  model?: string | null
  image_ids?: string[]
  email_summaries?: EmailSummary[]
  drive_files?: DriveFileSummary[]
  calendar_events?: CalendarEventSummary[]
}

// Suggestion chips for the empty-state hero. Picking one drops its text
// into the composer (no auto-send — the user stays in control).
/** Must match `MAX_CHAT_MESSAGE_CHARS` in app/api/chat.py. */
const MAX_CHAT_MESSAGE_CHARS = 100_000

const CHIP_KEYS = ["chat.chip.plan", "chat.chip.email", "chat.chip.recap", "chat.chip.image"]

function greeting(): string {
  const h = new Date().getHours()
  if (h < 5) return t("chat.greeting.night")
  if (h < 12) return t("chat.greeting.morning")
  if (h < 18) return t("chat.greeting.afternoon")
  return t("chat.greeting.evening")
}

function ChatPage() {
  const t = useT()
  const me = auth.me()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { c: convFromUrl, q: prefill } = Route.useSearch()
  const [input, setInput] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [convId, setConvId] = useState<string | null>(null)
  const [messages, setMessages] = useState<UIMessage[]>([])
  const [recording, setRecording] = useState(false)
  const [transcribing, setTranscribing] = useState(false)
  const [model, setModel] = useState<string>(() => getStoredModel())
  const [modelOpen, setModelOpen] = useState(false)
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const [files, setFiles] = useState<AttChip[]>([])
  const [image, setImage] = useState<ImageAtt | null>(null)
  const [uploading, setUploading] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  // Turn currently streaming into the thread (Stop button target).
  const [activeTurn, setActiveTurn] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  // Set right before we assign convId for a thread we created ourselves,
  // so the history loader doesn't wipe the streaming bubble.
  const skipNextLoadRef = useRef(false)
  // "/name" at the start of the composer → prompt-template picker.
  const slashQuery = useMemo(() => {
    const m = /^\/([\w-]*)$/.exec(input)
    return m ? m[1].toLowerCase() : null
  }, [input])
  const [slashIdx, setSlashIdx] = useState(0)
  const templatesQ = useQuery({
    queryKey: ["prompt-templates", me?.id],
    queryFn: () => api<PromptTemplateOut[]>(`/api/dashboard/users/${me?.id}/prompt_templates`),
    enabled: !!me?.id && slashQuery !== null,
    staleTime: 60_000,
  })
  const slashMatches = useMemo(() => {
    if (slashQuery === null) return []
    const all = templatesQ.data ?? []
    return all.filter((p) => !slashQuery || p.slug.toLowerCase().startsWith(slashQuery) || p.title.toLowerCase().includes(slashQuery)).slice(0, 8)
  }, [slashQuery, templatesQ.data])
  useEffect(() => { setSlashIdx(0) }, [slashQuery])

  function pickTemplate(p: PromptTemplateOut) {
    setInput(p.body)
    focusComposer()
  }
  const recorderRef = useRef<VoiceRecorder | null>(null)
  // Hands-free mode: VoiceLoop listens, we send, Lucy's reply is spoken, then we listen again.
  const loopRef = useRef<VoiceLoop | null>(null)
  const [voiceMode, setVoiceMode] = useState(false)
  const [voiceState, setVoiceState] = useState<"listening" | "speech" | "transcribing" | "paused" | "off">("off")
  const voiceModeRef = useRef(false)
  voiceModeRef.current = voiceMode
  // What the voice stage shows: your last utterance, Lucy's reply as it
  // streams, and whether she is mid-sentence.
  const [docsOpen, setDocsOpen] = useState(false)
  const [voiceUser, setVoiceUser] = useState("")
  const [voiceLucy, setVoiceLucy] = useState("")
  const [voiceMuted, setVoiceMuted] = useState(false)
  // Video call with Lucy: the button only shows when the plan allows it.
  const [callOpen, setCallOpen] = useState(false)
  const [threadReload, setThreadReload] = useState(0)
  const callStatus = useQuery({
    queryKey: ["call-status"],
    queryFn: () => api<CallStatusOut>("/api/call/status"),
    enabled: !!me?.id,
    staleTime: 60_000,
  })
  const voiceMutedRef = useRef(false)
  voiceMutedRef.current = voiceMuted
  const [lucyTalking, setLucyTalking] = useState(false)
  const voiceWaiting = useRef(false)
  const micMeter = useCallback(() => loopRef.current?.meter() ?? 0, [])
  // Last thing Lucy said out loud — a transcript that matches it is the
  // phone's speaker leaking back into the mic, not the user.
  const lastSpokenRef = useRef("")
  // Sentence-by-sentence speech while the reply streams (voice mode only).
  const speechRef = useRef<SpeechQueue | null>(null)
  const scroller = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  // Guards the one-time auto-resume below so it doesn't fight an explicit
  // "new conversation" (which sets convId back to null on purpose).
  const hydratedRef = useRef(false)
  const convIdRef = useRef<string | null>(null)
  convIdRef.current = convId

  function focusComposer() {
    requestAnimationFrame(() => textareaRef.current?.focus())
  }

  // Grow the composer with its content (capped by max-h in the class).
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`
  }, [input])

  // ---- attachments -------------------------------------------------
  async function addFiles(list: FileList | File[]) {
    if (!me) return
    const arr = Array.from(list)
    for (const f of arr) {
      if (IMAGE_TYPES.includes(f.type)) {
        if (f.size > 8 * 1024 * 1024) { setError(`${f.name}: image too large (max 8 MB).`); continue }
        const b64 = await fileToBase64(f)
        setImage((prev) => {
          if (prev) URL.revokeObjectURL(prev.previewUrl)
          return { b64, mediaType: f.type, previewUrl: URL.createObjectURL(f), name: f.name }
        })
        continue
      }
      const ext = (f.name.split(".").pop() || "").toLowerCase()
      if (!DOC_EXT.includes(ext)) { setError(`${f.name}: unsupported type. Use ${DOC_EXT.join(", ")} or an image.`); continue }
      if (f.size > MAX_UPLOAD_BYTES) { setError(`${f.name}: too large (max 25 MB).`); continue }
      setUploading((n) => n + 1)
      try {
        const fd = new FormData()
        fd.append("file", f)
        const out = await api<{ id: string; filename: string; size_bytes: number }>(
          "/api/dashboard/attachments", { method: "POST", rawBody: fd, query: { user_id: me.id } },
        )
        setFiles((prev) => [...prev, { id: out.id, name: out.filename, size: out.size_bytes }])
        setError(null)
      } catch (e) {
        setError((e as { detail?: string }).detail || `Upload failed for ${f.name}.`)
      } finally {
        setUploading((n) => n - 1)
      }
    }
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }

  function clearImage() {
    setImage((prev) => { if (prev) URL.revokeObjectURL(prev.previewUrl); return null })
  }

  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = Array.from(e.clipboardData?.files || [])
    if (items.length === 0) return
    e.preventDefault()
    void addFiles(items)
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer?.files?.length) void addFiles(e.dataTransfer.files)
  }

  async function toggleVoiceMode() {
    if (voiceMode) {
      loopRef.current?.stop()
      loopRef.current = null
      speechRef.current?.abort()
      speechRef.current = null
      stopSpeaking()
      setVoiceMode(false)
      setVoiceState("off")
      disableOutputMeter()
      setVoiceMuted(false)
      setVoiceUser("")
      setVoiceLucy("")
      return
    }
    setError(null)
    // Still inside the click: the output meter's AudioContext must start here.
    void enableOutputMeter()
    const loop = new VoiceLoop(
      (text) => {
        if (looksLikeEcho(text, lastSpokenRef.current)) { loopRef.current?.resume(); return }
        setVoiceUser(text)
        setVoiceLucy("")
        voiceWaiting.current = true
        void sendMessage(text)
      },
      (s) => setVoiceState(s),
      (msg) => setError(`Voice: ${msg}`),
    )
    try {
      await loop.start()
      loopRef.current = loop
      setVoiceMode(true)
      void prefetchFillers(getLocale())
    } catch {
      setError("Microphone access denied. Allow it in your browser settings.")
    }
  }

  // When Lucy stops talking, go back to listening — after a beat, so the
  // tail of the playback doesn't get recorded as the next utterance.
  useEffect(() => onSpeakingChange((key) => {
    setLucyTalking(key !== null)
    if (key === null && voiceModeRef.current) setTimeout(() => { if (!voiceMutedRef.current) loopRef.current?.resume() }, 700)
  }), [])
  function toggleVoiceMute() {
    if (voiceMuted) { setVoiceMuted(false); loopRef.current?.resume() }
    else { setVoiceMuted(true); loopRef.current?.pause() }
  }
  useEffect(() => () => { loopRef.current?.stop() }, [])

  async function stopTurn() {
    const tid = activeTurn
    abortRef.current?.abort()
    if (!tid) return
    try {
      await api(`/api/chat/turns/${tid}/cancel`, { method: "POST" })
    } catch (e) {
      const err = e as { status?: number }
      if (err.status !== 409) setError("Couldn't stop the turn.")
    }
  }

  function pickModel(slot: string) {
    setModel(slot)
    setStoredModel(slot)
    setModelOpen(false)
  }

  const convsQ = useQuery({
    queryKey: ["conversations", me?.id],
    queryFn: () => api<ConversationOut[]>(
      `/api/dashboard/users/${me?.id}/conversations`,
      { query: { limit: 50 } },
    ),
    enabled: !!me?.id,
    staleTime: 8_000,
  })
  const conversations = convsQ.data ?? []
  const currentConv = conversations.find((c) => c.id === convId) ?? null

  function openConv(id: string) {
    if (id === convId) {
      setSwitcherOpen(false)
      focusComposer()
      return
    }
    hydratedRef.current = true
    setConvId(id)
    setMessages([])
    setError(null)
    void nav({ to: "/chat", search: { c: id }, replace: true })
    setSwitcherOpen(false)
    focusComposer()
  }

  // ?q=… (from the welcome flow's starter chips) drops text into the
  // composer as a fresh thread; the user still presses Send.
  useEffect(() => {
    if (!prefill) return
    hydratedRef.current = true
    setConvId(null)
    setMessages([])
    setInput(prefill)
    void nav({ to: "/chat", search: {}, replace: true })
    focusComposer()
  }, [prefill])  // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from /conversations?c=… or first-load resume of the latest
  // thread. hydratedRef stops "New conversation" from being overwritten.
  useEffect(() => {
    if (prefill) return
    if (convFromUrl) {
      setConvId(convFromUrl)
      hydratedRef.current = true
      return
    }
    if (!me?.id || hydratedRef.current || convId) return
    hydratedRef.current = true
    api<ConversationOut[]>(`/api/dashboard/users/${me.id}/conversations`, {
      query: { limit: 1 },
    }).then((rows) => {
      if (rows.length > 0) setConvId(rows[0].id)
    }).catch(() => undefined)
  }, [me?.id, convId, convFromUrl])

  // Re-load conversation history when convId changes (resume / switch /
  // first-load hydration above). Pending turns become assistant placeholders.
  useEffect(() => {
    if (!convId || !me?.id) { setMessages([]); return }
    if (skipNextLoadRef.current) { skipNextLoadRef.current = false; return }
    let cancelled = false
    Promise.all([
      api<MessageOut[]>(`/api/dashboard/conversations/${convId}/messages`),
      api<ChatTurnOut[]>(
        `/api/dashboard/users/${me.id}/chat-turns`,
        { query: { conversation_id: convId } },
      ),
    ]).then(([rows, turns]) => {
      if (cancelled) return
      const msgs: UIMessage[] = rows.map((m) => ({
        id: m.id,
        role: m.role === "tool" ? "system" : m.role,
        text: m.text,
        provider: m.provider,
        model: m.model,
        image_ids: m.image_ids,
        output_attachment_ids: (m.meta as { attachment_ids?: string[] } | null)?.attachment_ids,
        email_summaries: m.email_summaries,
        drive_files: m.drive_files,
        calendar_events: m.calendar_events,
        feedback: ((m.meta as { feedback?: { rating?: "up" | "down" } } | null)?.feedback?.rating) ?? null,
        image_backends: Array.from(new Set(((m.meta as { images?: { provider?: string | null; model?: string | null }[] } | null)?.images ?? [])
          .map((i) => [i.provider, i.model].filter(Boolean).join(" · ")).filter(Boolean))),
      }))
      for (const t of turns) {
        if (t.status !== "queued" && t.status !== "running") continue
        if (msgs.some((m) => m.turn_id === t.id || m.id === `pending-${t.id}`)) continue
        msgs.push({
          id: `pending-${t.id}`,
          role: "assistant",
          text: "",
          pending: true,
          turn_id: t.id,
          pendingStatus: t.status,
        })
      }
      setMessages(msgs)
    }).catch(() => undefined)
    return () => { cancelled = true }
  }, [convId, me?.id, threadReload])

  // Auto-scroll the message scroller on new messages so the freshest
  // bubble sits just above the composer.
  useEffect(() => {
    const el = scroller.current
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
    })
  }, [messages.length])

  // Live stream — apply assistant replies to the open thread; badge the list.
  useEffect(() => {
    return dashboardStream.subscribe((evt) => {
      const p = (evt.payload || {}) as Record<string, unknown>
      const cid = String(p.conversation_id || "")
      const open = convIdRef.current
      if (evt.type === "message" && cid === open && p.role === "assistant") {
        const text = String(p.text || "")
        const mid = String(p.id || "")
        setMessages((prev) => {
          if (mid && prev.some((m) => m.id === mid)) return prev
          const idx = prev.findIndex((m) => m.pending && m.role === "assistant")
          const next = {
            id: mid || `a-${Date.now()}`,
            role: "assistant" as const,
            text,
            provider: (p.meta as { provider?: string } | undefined)?.provider,
            model: (p.meta as { model?: string } | undefined)?.model,
            image_ids: (p.meta as { image_ids?: string[] } | undefined)?.image_ids,
            output_attachment_ids: (p.meta as { attachment_ids?: string[] } | undefined)?.attachment_ids,
            email_summaries: (p.meta as { email_summaries?: EmailSummary[] } | undefined)?.email_summaries,
            drive_files: (p.meta as { drive_files?: DriveFileSummary[] } | undefined)?.drive_files,
            calendar_events: (p.meta as { calendar_events?: CalendarEventSummary[] } | undefined)?.calendar_events,
            image_backends: ((p.meta as { images?: { provider?: string | null; model?: string | null }[] } | undefined)?.images ?? [])
              .map((i) => [i.provider, i.model].filter(Boolean).join(" · ")).filter(Boolean),
          }
          if (idx >= 0) {
            const copy = prev.slice()
            copy[idx] = next
            return copy
          }
          return [...prev, next]
        })
      }
      if ((evt.type === "turn_started" || evt.type === "turn_queued") && cid === open) {
        const tid = String(p.turn_id || "")
        const running = evt.type === "turn_started"
        setMessages((prev) => {
          if (prev.some((m) => m.turn_id === tid)) {
            return prev.map((m) => (m.turn_id === tid && running ? { ...m, pendingStatus: "running" } : m))
          }
          // Our own freshly-sent turn: attach the id to the last untagged pending bubble.
          const idx = prev.map((m) => !!(m.pending && m.role === "assistant" && !m.turn_id)).lastIndexOf(true)
          if (idx < 0) return prev
          const copy = prev.slice()
          copy[idx] = { ...copy[idx], turn_id: tid, ...(running ? { pendingStatus: "running" as const } : {}) }
          return copy
        })
      }
      if (evt.type === "turn_failed" && cid === open) {
        const tid = String(p.turn_id || "")
        const err = String(p.error || "Lucy couldn't complete this turn.")
        setMessages((prev) => prev.map((m) =>
          m.turn_id === tid ? { ...m, pending: false, failed: true, text: err } : m,
        ))
      }
      if (evt.type === "message" || evt.type === "message_created" || evt.type === "conversation_created"
          || evt.type === "turn_queued" || evt.type === "turn_started"
          || evt.type === "turn_completed" || evt.type === "turn_failed") {
        qc.invalidateQueries({ queryKey: ["conversations"] })
        qc.invalidateQueries({ queryKey: ["chat-turns"] })
      }
      if (evt.type === "memory_added" || evt.type === "memory_created" || evt.type === "memory_forgotten" || evt.type === "profile_fact_updated") {
        qc.invalidateQueries({ queryKey: ["memories"] })
      }
    })
  }, [qc])

  async function sendMessage(text: string) {
    if ((!text.trim() && files.length === 0 && !image) || !me) return
    if (uploading > 0) { setError("Still uploading — one moment."); return }
    if (!text.trim()) text = image ? "What do you see in this image?" : "Please read the attached document(s)."
    if (text.length > MAX_CHAT_MESSAGE_CHARS) {
      setError(
        `Message is too long (${text.length.toLocaleString()} characters). ` +
        `Keep it under ${MAX_CHAT_MESSAGE_CHARS.toLocaleString()}.`,
      )
      return
    }
    setError(null)
    const userMsgId = `u-${Date.now()}`
    const pendingId = `a-${Date.now()}`
    const sentFiles = files
    const sentImage = image
    setMessages((prev) => [
      ...prev,
      {
        id: userMsgId, role: "user", text,
        attachments: sentFiles.length ? sentFiles : undefined,
        image_preview: sentImage?.previewUrl,
      },
      { id: pendingId, role: "assistant", text: "", pending: true, pendingStatus: "queued" },
    ])
    setInput("")
    setFiles([])
    setImage(null)
    focusComposer()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    // Voice mode: speak sentences as they stream; a filler covers a slow start.
    let spoken = ""          // sentences already handed to the queue
    let pendingTail = ""
    if (voiceModeRef.current) {
      loopRef.current?.pause()
      speechRef.current?.abort()
      const q = new SpeechQueue(getLocale(), () => { if (voiceModeRef.current && !voiceMutedRef.current) setTimeout(() => loopRef.current?.resume(), 400) })
      speechRef.current = q
      q.start()
    }
    // Patch the pending assistant bubble for this send. It may have been
    // re-keyed to `pending-<turn>` by the time deltas arrive, so match on
    // turn id first, then the original id, then the last pending bubble.
    let turnId: string | null = null
    const patchPending = (fn: (m: UIMessage) => UIMessage) =>
      setMessages((prev) => {
        let idx = prev.findIndex((m) => (turnId && m.turn_id === turnId) || m.id === pendingId)
        if (idx < 0) idx = prev.map((m) => !!(m.pending && m.role === "assistant")).lastIndexOf(true)
        if (idx < 0) return prev
        const copy = prev.slice()
        copy[idx] = fn(copy[idx])
        return copy
      })
    try {
      const body: ChatIn = {
        message: text,
        user_id: me.id,
        conversation_id: convId,
        // No active thread on the client (fresh page with no history, or
        // an explicit "New") → tell the backend not to resume the last one.
        new_conversation: !convId,
        channel: "web",
        attachment_ids: sentFiles.map((f) => f.id),
        image_b64: sentImage?.b64 ?? null,
        image_media_type: sentImage?.mediaType ?? null,
        // "auto" is the server default — only send a slot when the
        // user explicitly pinned a model.
        model: model === "auto" ? null : model,
        wait: false,
      }
      const res = await rawFetch("/api/chat/stream", { method: "POST", body, signal: ctrl.signal })
      if (!res.ok || !res.body) {
        const j = await res.json().catch(() => null) as { detail?: unknown } | null
        const detail = typeof j?.detail === "string" ? j.detail : JSON.stringify(j?.detail ?? "")
        throw Object.assign(new Error(detail || `HTTP ${res.status}`), { status: res.status, detail })
      }
      const origin = convId
      let accumulated = ""
      let finished = false
      for await (const evt of readSSE(res.body)) {
        if (evt.type === "start") {
          const cid = String(evt.conversation_id)
          const umid = evt.user_message_id ? String(evt.user_message_id) : null
          if (umid) setMessages((prev) => prev.map((m) => (m.id === userMsgId ? { ...m, id: umid } : m)))
          if (!origin && !convIdRef.current) {
            skipNextLoadRef.current = true
            setConvId(cid)
            void nav({ to: "/chat", search: { c: cid }, replace: true })
          }
        } else if (evt.type === "delta") {
          if (convIdRef.current !== (origin ?? convIdRef.current)) continue
          accumulated += String(evt.text || "")
          if (speechRef.current) {
            voiceWaiting.current = false
            setVoiceLucy(accumulated)
            const [sents, rest] = splitSentences(accumulated.slice(spoken.length))
            for (const sent of sents) speechRef.current.say(sent)
            spoken = accumulated.slice(0, accumulated.length - rest.length)
            pendingTail = rest
          }
          patchPending((m) => ({ ...m, pending: true, streaming: true, pendingStatus: "running", text: accumulated }))
        } else if (evt.type === "done") {
          finished = true
          const out = evt as unknown as ChatOut
          turnId = out.turn_id || turnId
          if (voiceModeRef.current && (out.reply || accumulated)) {
            const full = out.reply || accumulated
            lastSpokenRef.current = full
            voiceWaiting.current = false
            setVoiceLucy(full)
            const q = speechRef.current
            if (q) {
              // Whatever streaming didn't cover (tail, or a provider retry that
              // changed the text) is spoken from the authoritative reply.
              const tail = full.startsWith(spoken) ? full.slice(spoken.length) : (spoken ? "" : full)
              if (tail.trim()) q.say(tail)
              else if (!spoken && pendingTail.trim()) q.say(pendingTail)
              q.close()
            } else {
              loopRef.current?.pause()
              void speak(out.assistant_message_id || `voice-${Date.now()}`, full)
                .catch(() => loopRef.current?.resume())
            }
          }
          patchPending((m) => ({
            ...m,
            id: out.assistant_message_id || m.id,
            turn_id: out.turn_id || m.turn_id,
            pending: false,
            streaming: false,
            text: out.reply || accumulated,
            provider: out.provider,
            model: out.model,
            image_ids: out.image_ids,
            output_attachment_ids: (out as { attachment_ids?: string[] }).attachment_ids,
            email_summaries: out.email_summaries,
            drive_files: out.drive_files,
            calendar_events: out.calendar_events,
          }))
        } else if (evt.type === "error") {
          finished = true
          speechRef.current?.close()
          const msg = String(evt.message || "Lucy couldn't complete this turn.")
          // "Still working" = stream deadline, not a failure: leave the bubble
          // pending; the WS `message` event will fill it in.
          if (/still working/i.test(msg)) {
            patchPending((m) => ({ ...m, streaming: false }))
          } else {
            patchPending((m) => ({ ...m, pending: false, streaming: false, failed: true, text: msg }))
          }
        }
        // The stream doesn't carry the turn id until `done`; learn it from
        // the thread (WS turn_* events or the history loader set it).
        if (!turnId) {
          const t = messages.find((m) => m.pending && m.turn_id)?.turn_id
          if (t) turnId = t
        }
      }
      if (!finished) {
        // Connection closed without a terminal event (proxy hiccup). The
        // turn is still running server-side; WS will deliver the reply.
        patchPending((m) => ({ ...m, streaming: false }))
      }
    } catch (e) {
      speechRef.current?.abort()
      if (voiceModeRef.current) setTimeout(() => loopRef.current?.resume(), 400)
      if ((e as Error).name === "AbortError") {
        // Stop pressed: the cancel endpoint flips the row; WS turn_failed
        // (status=cancelled) renders "Stopped." with a Retry.
        patchPending((m) => ({ ...m, streaming: false, pending: true }))
        return
      }
      const err = e as { status?: number; detail?: string }
      const msg =
        err.status === 402 ? "You've hit your tier limit. Upgrade in Billing to continue."
        : err.status === 429 ? "Slow down a bit — rate limit hit."
        : err.status === 503 ? "Lucy is paused. Resume in Account."
        : err.status === 502 ? (err.detail || "Lucy can't reach an LLM provider right now.")
        : err.status === 500 ? "Something broke on the server. Check the logs or try again in a moment."
        : err.status === 422
          ? (/too long|at most/i.test(String(err.detail))
            ? `Message is too long. Keep it under ${MAX_CHAT_MESSAGE_CHARS.toLocaleString()} characters.`
            : (err.detail || "That request wasn't accepted."))
        : err.detail || "Send failed."
      setError(msg)
      setMessages((prev) => prev.filter((m) => m.id !== pendingId && m.id !== userMsgId))
    } finally {
      abortRef.current = null
      focusComposer()
    }
  }

  // The Stop button needs the running turn id; the thread learns it from
  // WS `turn_started` / the history loader. Track the newest pending one.
  useEffect(() => {
    const t = [...messages].reverse().find((m) => m.pending && m.role === "assistant")
    setActiveTurn(t?.turn_id ?? (t ? "" : null))
  }, [messages])

  async function retryTurn(turnId: string) {
    try {
      await api<ChatOut>(`/api/chat/turns/${turnId}/retry`, { method: "POST" })
      setMessages((prev) => prev.map((m) =>
        m.turn_id === turnId
          ? { ...m, pending: true, failed: false, text: "", pendingStatus: "queued" }
          : m,
      ))
    } catch (e) {
      setError((e as { detail?: string }).detail || "Retry failed.")
    }
  }

  function newConversation() {
    setConvId(null)
    setMessages([])
    setError(null)
    setInput("")
    setSwitcherOpen(false)
    void nav({ to: "/chat", search: { c: undefined }, replace: true })
    focusComposer()
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault()
        setSwitcherOpen((o) => !o)
        setModelOpen(false)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  function pickChip(text: string) {
    setInput(text)
    textareaRef.current?.focus()
  }

  async function startVoice() {
    setError(null)
    const rec = new VoiceRecorder()
    recorderRef.current = rec
    try {
      await rec.start()
      setRecording(true)
    } catch (e) {
      setError("Microphone access denied. Allow it in your browser settings.")
      recorderRef.current = null
    }
  }

  async function stopVoice() {
    const rec = recorderRef.current
    if (!rec) return
    setRecording(false)
    setTranscribing(true)
    try {
      const text = await rec.stopAndTranscribe()
      if (text) setInput((prev) => prev ? `${prev} ${text}` : text)
      focusComposer()
    } catch (e) {
      setError(`Voice failed: ${(e as Error).message}`)
    } finally {
      setTranscribing(false)
      recorderRef.current = null
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (slashQuery !== null && slashMatches.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSlashIdx((i) => (i + 1) % slashMatches.length); return }
      if (e.key === "ArrowUp") { e.preventDefault(); setSlashIdx((i) => (i - 1 + slashMatches.length) % slashMatches.length); return }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pickTemplate(slashMatches[slashIdx]); return }
      if (e.key === "Escape") { e.preventDefault(); setInput(""); return }
    }
    // Enter sends; Shift+Enter inserts a newline (the messaging-app
    // convention people expect). ⌘/Ctrl+Enter still sends as a
    // muscle-memory fallback.
    if (e.key === "Enter" && !e.shiftKey && !e.altKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  const placeholder = useMemo(
    () => convId ? t("chat.placeholder.continue") : t("chat.placeholder.new"),
    [convId, t],
  )

  const name = me?.display_name || me?.email?.split("@")[0] || ""
  const empty = messages.length === 0
  const activeModel = MODEL_OPTIONS.find((m) => m.slot === model)

  // Full-bleed flex column (breaks out of the shell's padding like the
  // brain route). Messages scroll in the middle; the glass composer is
  // pinned to the bottom and always reachable.
  return (
    <div className="-m-4 md:-m-8 relative flex h-[calc(100dvh-52px)] md:h-[100dvh] flex-col overflow-hidden">
      {callOpen && (
        <VideoCall
          conversationId={convId}
          onClose={({ conversationId: cid, turns }) => {
            setCallOpen(false)
            qc.invalidateQueries({ queryKey: ["call-status"] })
            qc.invalidateQueries({ queryKey: ["conversations"] })
            if (cid && cid !== convIdRef.current) {
              setConvId(cid)
              void nav({ to: "/chat", search: { c: cid }, replace: true })
            } else if (turns > 0) {
              setThreadReload((n) => n + 1)
            }
          }}
        />
      )}
      {voiceMode && (
        <VoiceStage
          state={voiceStageState(voiceState, voiceMuted, lucyTalking, voiceWaiting.current || activeTurn !== null)}
          userText={voiceUser}
          lucyText={voiceLucy}
          micLevel={micMeter}
          muted={voiceMuted}
          onToggleMute={toggleVoiceMute}
          onEnd={() => void toggleVoiceMode()}
          onKeyboard={() => void toggleVoiceMode()}
        />
      )}
      {/* Ambient gradient blobs — only in the calm empty state. */}
      {empty && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-[12%] top-[18%] h-72 w-72 animate-blob-1 rounded-full bg-primary/20 blur-[90px]" />
          <div className="absolute right-[14%] top-[30%] h-64 w-64 animate-blob-2 rounded-full bg-violet/15 blur-[90px]" />
          <div className="absolute bottom-[16%] left-[40%] h-56 w-56 animate-blob-3 rounded-full bg-gold/10 blur-[90px]" />
        </div>
      )}

      <SessionBar
        currentId={convId}
        currentTitle={currentConv?.title ?? (convId ? t("chat.untitled") : t("chat.newConversation"))}
        conversations={conversations}
        open={switcherOpen}
        onToggle={() => { setSwitcherOpen((o) => !o); setModelOpen(false) }}
        onClose={() => setSwitcherOpen(false)}
        onOpen={openConv}
        onNew={newConversation}
              onDocs={() => setDocsOpen(true)}
      />
      <ConversationDocs conversationId={convId} open={docsOpen} onClose={() => setDocsOpen(false)} />
      {/* Soft fade so text slides under the header instead of being cut. */}
      {!empty && <div className="pointer-events-none relative z-10 -mb-6 h-6 bg-gradient-to-b from-background to-transparent" />}

      {/* Scroll region: hero when empty, otherwise the message stream. */}
      <div
        ref={scroller}
        className="chat-scroll relative z-0 flex-1 overflow-y-auto"
      >
        {empty ? (
          <div className="flex h-full flex-col items-center justify-center px-5 md:px-8 text-center">
            <div className="mb-3.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
              {greeting()}
            </div>
            <h1 className="m-0 text-[34px] md:text-[44px] leading-[1.05]">
              {t("chat.hero", { name: name ? `, ${name}` : "" })}
            </h1>
            <p className="mt-3.5 max-w-[460px] text-sm leading-relaxed text-muted-foreground">
              {t("chat.heroSub")}
            </p>
            <div className="mt-8 flex max-w-[600px] flex-wrap justify-center gap-2.5">
              {CHIP_KEYS.map((k) => t(k)).map((c) => (
                <button
                  key={c}
                  onClick={() => pickChip(c)}
                  className="inline-flex items-center gap-2 rounded-full bg-card px-3.5 py-2.5 text-[13px] text-foreground/85 ring-1 ring-inset ring-border transition hover:ring-primary/40 hover:bg-secondary"
                >
                  <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-primary" />
                  {c}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mx-auto w-full max-w-[860px] space-y-3.5 px-4 pb-8 pt-6 md:px-6 md:pt-7">
            {messages.map((m) => <MessageBubble key={m.id} m={m} onRetry={retryTurn} />)}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="relative z-10 shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))] md:pb-6 pt-2">
        <div className="mx-auto w-full max-w-[860px] px-3 md:px-6">
          {error && <div className="mb-2"><Toast variant="destructive">{error}</Toast></div>}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`rounded-[22px] bg-card px-3.5 pb-2.5 pt-3 ring-1 ring-inset transition ${dragOver ? "ring-primary/60 bg-primary/5" : "ring-border"}`}
          >
            {(files.length > 0 || image || uploading > 0) && (
              <div className="mb-2.5 flex flex-wrap items-center gap-2">
                {image && (
                  <span className="relative inline-flex items-center gap-2 rounded-lg border border-border bg-foreground/[0.04] p-1 pr-2 text-xs">
                    <img src={image.previewUrl} alt={image.name} className="h-10 w-10 rounded object-cover" />
                    <span className="max-w-[140px] truncate">{image.name}</span>
                    <button onClick={clearImage} title="Remove image" className="rounded p-0.5 text-muted-foreground hover:text-foreground"><XIcon className="h-3.5 w-3.5" /></button>
                  </span>
                )}
                {files.map((f) => (
                  <span key={f.id} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-foreground/[0.04] px-2 py-1 text-xs">
                    <FileIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="max-w-[160px] truncate">{f.name}</span>
                    <span className="text-muted-foreground">{formatBytes(f.size)}</span>
                    <button onClick={() => removeFile(f.id)} title="Remove" className="rounded p-0.5 text-muted-foreground hover:text-foreground"><XIcon className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
                {uploading > 0 && <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Spinner /> {t("chat.uploading")}</span>}
              </div>
            )}
            {slashQuery !== null && (
              <div className="mb-2 overflow-hidden rounded-xl border border-border bg-card">
                {slashMatches.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">{t("slash.hint")}</div>
                ) : slashMatches.map((p, i) => (
                  <button
                    key={p.id}
                    onMouseDown={(e) => { e.preventDefault(); pickTemplate(p) }}
                    className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm ${i === slashIdx ? "bg-primary/15" : "hover:bg-foreground/[0.06]"}`}
                  >
                    <WandIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0">
                      <span className="font-mono text-xs text-primary">/{p.slug}</span>
                      <span className="ml-2 text-foreground">{p.title}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">{p.body}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={input}
              placeholder={placeholder}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              rows={1}
              autoFocus
              aria-label="Message"
              className="max-h-[220px] min-h-[28px] w-full resize-none border-none bg-transparent px-1 pb-1 pt-1 text-[16px] leading-[1.5] md:text-[15px] text-foreground outline-none placeholder:text-muted-foreground"
            />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={[...DOC_EXT.map((e) => `.${e}`), ...IMAGE_TYPES].join(",")}
              className="hidden"
              onChange={(e) => { if (e.target.files) void addFiles(e.target.files); e.target.value = "" }}
            />

            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-1.5">
            {/* Model selector pill + popover */}
            <div className="relative flex">
              <button
                onClick={() => setModelOpen((o) => !o)}
                className="inline-flex h-[34px] items-center gap-2 rounded-full border border-border bg-foreground/[0.04] px-3 text-xs font-medium text-foreground/80 transition hover:bg-foreground/[0.06]"
                title={activeModel?.hint}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full bg-primary"
                  style={{ boxShadow: "0 0 8px rgba(192,140,255,0.8)" }}
                />
                {activeModel?.label ?? "Auto"}
                <ChevronDownIcon
                  className={`h-3 w-3 transition-transform ${modelOpen ? "rotate-180" : ""}`}
                />
              </button>
              {modelOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setModelOpen(false)} />
                  <div className="absolute bottom-[calc(100%+8px)] left-0 z-30 w-[320px] rounded-2xl border border-border bg-card p-1.5 shadow-[0_16px_44px_rgba(0,0,0,0.55)]">
                    {MODEL_OPTIONS.map((m) => {
                      const active = m.slot === model
                      return (
                        <button
                          key={m.slot}
                          onClick={() => pickModel(m.slot)}
                          className={`flex w-full flex-col gap-0.5 rounded-xl px-3 py-2 text-left transition ${active ? "bg-primary/12" : "hover:bg-foreground/[0.06]"}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[13.5px] font-medium text-foreground">{m.label}</span>
                            {active && <CheckIcon className="h-[15px] w-[15px] text-primary" />}
                          </div>
                          {m.hint && (
                            <span className="text-[11.5px] text-muted-foreground">{m.hint}</span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
                <button
                  onClick={recording ? stopVoice : startVoice}
                  disabled={transcribing}
                  title={recording ? "Stop & transcribe" : "Push-to-talk"}
                  className={`flex h-[34px] w-[34px] items-center justify-center rounded-full transition ${
                    recording
                      ? "bg-destructive/15 text-destructive"
                      : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                  } disabled:opacity-50`}
                >
                  {transcribing ? <Spinner /> : recording ? <StopCircleIcon className="h-[18px] w-[18px]" /> : <MicIcon className="h-[18px] w-[18px]" />}
                </button>
                <button
                  onClick={toggleVoiceMode}
                  title={voiceMode ? t("voice.off") : t("voice.on")}
                  aria-label={voiceMode ? t("voice.off") : t("voice.on")}
                  aria-pressed={voiceMode}
                  className={`flex h-[34px] items-center justify-center gap-1.5 rounded-full px-2 transition ${
                    voiceMode ? "bg-primary/15 text-primary" : "text-violet hover:bg-foreground/[0.06] hover:text-foreground"
                  }`}
                >
                  <HeadphonesIcon className="h-[18px] w-[18px]" />
                  {voiceMode && (
                    <span className="text-[11px]">
                      {voiceState === "speech" ? t("voice.hearing") : voiceState === "transcribing" ? t("voice.transcribing")
                        : voiceState === "paused" ? t("voice.speaking") : t("voice.listening")}
                    </span>
                  )}
                </button>
                {callStatus.data?.enabled && (
                  <button
                    onClick={() => { if (voiceMode) void toggleVoiceMode(); setCallOpen(true) }}
                    title={t("call.start")}
                    aria-label={t("call.start")}
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-full text-gold transition hover:bg-foreground/[0.06] hover:text-foreground"
                  >
                    <VideoIcon className="h-[18px] w-[18px]" />
                  </button>
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  title={t("chat.attach")}
                  aria-label={t("chat.attach")}
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-full text-muted-foreground transition hover:bg-foreground/[0.06] hover:text-foreground"
                >
                  <PaperclipIcon className="h-[18px] w-[18px]" />
                </button>
                {input.length > MAX_CHAT_MESSAGE_CHARS * 0.8 && (
                  <span className={`font-mono text-[11px] ${
                    input.length > MAX_CHAT_MESSAGE_CHARS ? "text-destructive" : "text-muted-foreground"
                  }`}>
                    {input.length.toLocaleString()} / {MAX_CHAT_MESSAGE_CHARS.toLocaleString()}
                  </span>
                )}
              </div>
              {activeTurn !== null ? (
                <button
                  onClick={stopTurn}
                  title={t("chat.stop")}
                  aria-label={t("chat.stop")}
                  className="flex h-[38px] w-[38px] items-center justify-center rounded-[12px] border border-border bg-foreground/[0.04] text-foreground transition hover:bg-destructive/20 hover:text-destructive"
                >
                  <SquareIcon className="h-[16px] w-[16px]" fill="currentColor" />
                </button>
              ) : (
                <button
                  onClick={() => sendMessage(input)}
                  disabled={(!input.trim() && files.length === 0 && !image) || input.length > MAX_CHAT_MESSAGE_CHARS || uploading > 0}
                  title={`${t("chat.send")} (Enter · Shift+Enter ↵)`}
                  aria-label={t("chat.send")}
                  className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_4px_14px_rgba(243,183,87,0.4)] transition hover:brightness-110 disabled:bg-primary/30 disabled:text-primary-foreground/70 disabled:shadow-none"
                >
                  <SendIcon className="h-[18px] w-[18px]" />
                </button>
              )}
            </div>
          </div>
          <div className="mt-2.5 text-center text-[11.5px] text-muted-foreground">
            {t("chat.disclaimer")}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Renders an authenticated image. The /api/dashboard/images/{id}
 * endpoint requires a Bearer token (legacy auth dep on the dashboard
 * router) — browsers can't attach that to a plain `<img src>`. Fetch
 * the bytes via `rawFetch` (which knows about the JWT), wrap in a
 * blob URL, and render. Revoke the blob URL on unmount so we don't
 * leak object URLs across navigations.
 */
function AuthedImage({ id }: { id: string }) {
  const [src, setSrc] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    let active = true
    let url: string | null = null
    rawFetch(`/api/dashboard/images/${id}`, { headers: vault.headers() }).then(async (r) => {
      if (!active) return
      if (r.status === 403) {
        setErr("locked")
        return
      }
      if (!r.ok) {
        setErr(`Image failed (${r.status})`)
        return
      }
      const blob = await r.blob()
      url = URL.createObjectURL(blob)
      setSrc(url)
    }).catch((e) => active && setErr((e as Error).message))
    return () => {
      active = false
      if (url) URL.revokeObjectURL(url)
    }
  }, [id])
  if (err === "locked") {
    return (
      <Link to="/media" className="flex h-24 w-48 items-center justify-center gap-2 rounded-md border bg-muted/40 text-xs text-muted-foreground hover:bg-muted">
        <LockIcon className="h-4 w-4" /> Hidden — unlock in Media
      </Link>
    )
  }
  if (err) return <div className="text-[10px] text-destructive">{err}</div>
  if (!src) return <div className="h-32 w-full rounded-md bg-muted/40 animate-pulse" />
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block">
      <img
        src={src}
        alt="generated"
        className="rounded-lg max-h-96 object-contain border border-border bg-background"
      />
    </a>
  )
}

/** Renders the structured email cards surfaced by gmail_search — same data
 *  the assistant summarized in text, but clickable (opens the Gmail thread)
 *  with unread/important state. Mirrors the AuthedImage attachment pattern. */
function EmailCards({ items }: { items: EmailSummary[] }) {
  return (
    <div className="mt-2.5 flex flex-col gap-2">
      {items.map((e, i) => {
        return (
          <a
            key={e.thread_id || i}
            href={e.open_url || undefined}
            target={e.open_url ? "_blank" : undefined}
            rel={e.open_url ? "noreferrer" : undefined}
            className={`group flex items-start gap-3 rounded-xl border border-border bg-foreground/[0.04] px-3.5 py-3 transition ${e.open_url ? "cursor-pointer hover:border-primary/30 hover:bg-foreground/[0.06]" : ""}`}
          >
            <span
              className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${e.is_unread ? "bg-primary" : "border border-border"}`}
              style={e.is_unread ? { boxShadow: "0 0 8px rgba(192,140,255,0.8)" } : undefined}
              title={e.is_unread ? "Unread" : "Read"}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className={`truncate text-[13.5px] ${e.is_unread ? "font-semibold text-foreground" : "font-medium text-foreground"}`}>
                  {e.from_name || e.from_email || "Unknown sender"}
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {e.received_at ? timeAgo(e.received_at) : ""}
                </span>
              </div>
              <div className="truncate text-[13px] text-foreground/80">{e.subject || "(no subject)"}</div>
              {e.snippet && (
                <div className="mt-0.5 line-clamp-1 text-[12px] text-muted-foreground">{e.snippet}</div>
              )}
              {(e.is_important || (e.message_count ?? 0) > 1 || e.account) && (
                <div className="mt-1.5 flex items-center gap-2">
                  {e.is_important && <Badge variant="warn">important</Badge>}
                  {(e.message_count ?? 0) > 1 && (
                    <span className="text-[10.5px] text-muted-foreground">{e.message_count} messages</span>
                  )}
                  {e.account && <span className="truncate text-[10.5px] text-[#6b6f88]">{e.account}</span>}
                </div>
              )}
            </div>
            {e.open_url && (
              <ArrowUpRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition group-hover:opacity-100" />
            )}
          </a>
        )
      })}
    </div>
  )
}

/** Drive file cards surfaced by gdrive_search — click opens the file in Drive. */
function DriveCards({ items }: { items: DriveFileSummary[] }) {
  return (
    <div className="mt-2.5 flex flex-col gap-2">
      {items.map((f, i) => (
        <a
          key={f.file_id || i}
          href={f.web_view_link || undefined}
          target={f.web_view_link ? "_blank" : undefined}
          rel={f.web_view_link ? "noreferrer" : undefined}
          className={`group flex items-center gap-3 rounded-xl border border-border bg-foreground/[0.04] px-3.5 py-2.5 transition ${f.web_view_link ? "cursor-pointer hover:border-primary/30 hover:bg-foreground/[0.06]" : ""}`}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/[0.10]">
            <FileIcon className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13.5px] font-medium text-foreground">{f.name || "Untitled"}</div>
            <div className="truncate text-[11.5px] text-muted-foreground">
              {[
                f.kind && f.kind !== "file" ? f.kind : null,
                f.owner_name || null,
                f.modified_at ? timeAgo(f.modified_at) : null,
                f.size_bytes ? formatBytes(f.size_bytes) : null,
              ].filter(Boolean).join(" · ")}
            </div>
          </div>
          {f.web_view_link && (
            <ArrowUpRightIcon className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition group-hover:opacity-100" />
          )}
        </a>
      ))}
    </div>
  )
}

function fmtEventWhen(e: CalendarEventSummary): string {
  if (!e.start) return ""
  try {
    const s = new Date(e.start)
    const dateOpts: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" }
    if (e.all_day) return s.toLocaleDateString(undefined, dateOpts)
    const timeOpts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" }
    let out = `${s.toLocaleDateString(undefined, dateOpts)}, ${s.toLocaleTimeString(undefined, timeOpts)}`
    if (e.end) out += ` – ${new Date(e.end).toLocaleTimeString(undefined, timeOpts)}`
    return out
  } catch {
    return e.start
  }
}

/** Calendar event cards surfaced by gcal_list — click opens the event. */
function CalendarCards({ items }: { items: CalendarEventSummary[] }) {
  const respColor: Record<string, string> = {
    accepted: "ok", declined: "destructive", tentative: "warn",
  }
  return (
    <div className="mt-2.5 flex flex-col gap-2">
      {items.map((e, i) => (
        <a
          key={e.event_id || i}
          href={e.html_link || undefined}
          target={e.html_link ? "_blank" : undefined}
          rel={e.html_link ? "noreferrer" : undefined}
          className={`group flex items-start gap-3 rounded-xl border border-border bg-foreground/[0.04] px-3.5 py-3 transition ${e.html_link ? "cursor-pointer hover:border-primary/30 hover:bg-foreground/[0.06]" : ""}`}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gold/20 bg-gold/[0.10]">
            <CalendarDaysIcon className="h-4 w-4 text-gold" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[13.5px] font-medium text-foreground">{e.title || "(no title)"}</span>
              {e.my_response && e.my_response !== "needsAction" && (
                <Badge variant={(respColor[e.my_response] as "ok" | "warn" | "destructive") || "muted"}>
                  {e.my_response}
                </Badge>
              )}
            </div>
            <div className="truncate text-[12px] text-foreground/80">{fmtEventWhen(e)}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
              {e.location && (
                <span className="inline-flex items-center gap-1 truncate"><MapPinIcon className="h-3 w-3 shrink-0" />{e.location}</span>
              )}
              {(e.attendee_count ?? 0) > 0 && (
                <span className="inline-flex items-center gap-1"><UsersIcon className="h-3 w-3" />{e.attendee_count}</span>
              )}
            </div>
          </div>
          {e.html_link && (
            <ArrowUpRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition group-hover:opacity-100" />
          )}
        </a>
      ))}
    </div>
  )
}

function SessionBar({
  currentId,
  currentTitle,
  conversations,
  open,
  onToggle,
  onClose,
  onOpen,
  onNew,
  onDocs,
}: {
  currentId: string | null
  currentTitle: string
  conversations: ConversationOut[]
  open: boolean
  onToggle: () => void
  onClose: () => void
  onOpen: (id: string) => void
  onNew: () => void
  onDocs?: () => void
}) {
  const [q, setQ] = useState("")
  const [hi, setHi] = useState(0)
  const searchRef = useRef<HTMLInputElement | null>(null)


  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const rows = needle
      ? conversations.filter((c) => (c.title || "untitled").toLowerCase().includes(needle))
      : conversations
    return [...rows].sort((a, b) => {
      const ar = a.active_turn ? (a.active_turn.status === "running" ? 0 : 1) : 2
      const br = b.active_turn ? (b.active_turn.status === "running" ? 0 : 1) : 2
      if (ar !== br) return ar - br
      return 0
    })
  }, [conversations, q])

  useEffect(() => {
    if (!open) return
    setQ("")
    setHi(0)
    requestAnimationFrame(() => searchRef.current?.focus())
  }, [open])

  useEffect(() => {
    setHi(0)
  }, [q])

  function pick(i: number) {
    const row = filtered[i]
    if (row) onOpen(row.id)
  }

  return (
    <div className="relative z-20 mx-auto flex w-full items-center gap-2 px-4 pb-2 pt-3 md:px-6">
      <div className="mr-2 hidden items-center gap-3 md:flex">
        <img src="/lucy-logo.png" alt="" className="h-10 w-10 rounded-full object-cover shadow-[0_0_20px_rgba(192,140,255,0.45)]" />
        <div className="flex flex-col gap-0.5">
          <span className="font-display text-[20px] leading-none">Lucy</span>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <span className="h-[7px] w-[7px] rounded-full bg-mint" />{t("chat.here")}
          </span>
        </div>
      </div>
      <div className="relative min-w-0">
        <button
          onClick={onToggle}
          title="Αλλαγή συζήτησης (⌘J)"
          className="inline-flex max-w-[220px] items-center gap-1 rounded-lg px-2 py-1 text-[13.5px] font-medium text-foreground/90 transition hover:bg-foreground/[0.06] sm:max-w-[380px]"
        >
          <span className="truncate">{currentTitle}</span>
          <ChevronDownIcon className={`h-3 w-3 shrink-0 text-muted-foreground transition ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={onClose} />
            <div className="absolute left-0 top-[calc(100%+8px)] z-40 w-[min(92vw,360px)] overflow-hidden rounded-2xl border border-border bg-card shadow-[0_16px_44px_rgba(0,0,0,0.55)]">
              <div className="flex items-center gap-2 border-b border-border px-3">
                <SearchIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault()
                      setHi((n) => Math.min(n + 1, Math.max(filtered.length - 1, 0)))
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault()
                      setHi((n) => Math.max(n - 1, 0))
                    } else if (e.key === "Enter") {
                      e.preventDefault()
                      pick(hi)
                    } else if (e.key === "Escape") {
                      e.preventDefault()
                      onClose()
                    }
                  }}
                  placeholder="Αναζήτηση συζητήσεων…"
                  className="h-10 flex-1 bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
                />
                <span className="rounded-md border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">⌘K</span>
              </div>
              <div className="max-h-[min(60vh,380px)] overflow-y-auto p-1.5">
                {filtered.length === 0 && (
                  <p className="px-3 py-4 text-[12px] text-muted-foreground">Καμία συζήτηση.</p>
                )}
                {filtered.map((c, i) => {
                  const active = c.id === currentId
                  const running = c.active_turn?.status === "running"
                  const queued = c.active_turn?.status === "queued"
                  return (
                    <button
                      key={c.id}
                      onMouseEnter={() => setHi(i)}
                      onClick={() => onOpen(c.id)}
                      className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left transition ${
                        i === hi ? "bg-foreground/[0.04]" : "hover:bg-foreground/[0.06]"
                      } ${active ? "ring-1 ring-inset ring-primary/30" : ""}`}
                    >
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${
                          running ? "animate-pulse bg-primary"
                            : queued ? "bg-muted-foreground/70"
                            : "bg-white/15"
                        }`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-foreground">
                          {c.title || "Untitled"}
                        </span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {running ? "γράφει…" : queued ? "στη σειρά…" : timeAgo(c.last_message_at || c.updated_at)}
                        </span>
                      </span>
                      {active && <CheckIcon className="h-3.5 w-3.5 shrink-0 text-primary" />}
                    </button>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {currentId && onDocs && (
        <button
          onClick={onDocs}
          title={t("att.docsTitle")}
          className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full bg-card px-3 py-1.5 text-xs font-medium text-foreground ring-1 ring-inset ring-border transition hover:bg-secondary"
        >
          <FileIcon className="h-3.5 w-3.5 text-violet" />
          {t("att.docs")}
        </button>
      )}
      <button
        onClick={onNew}
        className={`${currentId && onDocs ? "" : "ml-auto "}inline-flex shrink-0 items-center gap-1.5 rounded-full bg-card px-3 py-1.5 text-xs font-medium text-foreground ring-1 ring-inset ring-border transition hover:bg-secondary`}
      >
        <PlusIcon className="h-3.5 w-3.5 text-primary" />
        {t("chat.new")}
      </button>
    </div>
  )
}

function MessageBubble({ m, onRetry }: { m: UIMessage; onRetry?: (turnId: string) => void }) {
  const isUser = m.role === "user"
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} animate-fade-in`}>
      <div
        className={`min-w-0 break-words text-[15px] leading-[1.55] ${
          isUser
            ? "max-w-[88%] md:max-w-[75%] whitespace-pre-wrap rounded-[18px] rounded-br-[4px] bg-primary px-4 py-2.5 text-primary-foreground"
            : "max-w-[94%] md:max-w-full rounded-[18px] rounded-bl-[4px] bg-secondary px-5 py-3.5 text-foreground ring-1 ring-inset ring-border"
        }`}
      >
        {isUser && m.image_preview && (
          <img src={m.image_preview} alt="" className="mb-2 max-h-48 rounded-lg object-cover" />
        )}
        {isUser && m.attachments && m.attachments.length > 0 && (
          <div className="mb-1.5 flex flex-wrap gap-1.5">
            {m.attachments.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1 rounded-md bg-white/15 px-1.5 py-0.5 text-[11px]">
                <FileIcon className="h-3 w-3" />{a.name}
              </span>
            ))}
          </div>
        )}
        {m.pending && m.streaming && m.text ? (
          <div className="streaming-cursor">
            <Markdown text={m.text} />
          </div>
        ) : m.pending ? (
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <Spinner />
            {m.pendingStatus === "queued" ? t("chat.queued") : t("chat.thinking")}
          </span>
        ) : m.failed ? (
          <div className="space-y-2">
            <p className="text-destructive">{m.text || "Something went wrong."}</p>
            {m.turn_id && onRetry && (
              <button
                onClick={() => onRetry(m.turn_id!)}
                className="text-[12px] text-primary underline"
              >
                {t("chat.retry")}
              </button>
            )}
          </div>
        ) : isUser ? (
          m.text
        ) : (
          <Markdown text={m.text} />
        )}
        {!m.pending && m.image_ids && m.image_ids.length > 0 && (
          <div className="mt-2 grid grid-cols-1 gap-2">
            {m.image_ids.map((id) => <AuthedImage key={id} id={id} />)}
          </div>
        )}
        {!m.pending && m.output_attachment_ids && m.output_attachment_ids.length > 0 && (
          <div className="mt-2">
            {m.output_attachment_ids.map((id) => <AttachmentCard key={id} id={id} />)}
          </div>
        )}
        {!m.pending && m.email_summaries && m.email_summaries.length > 0 && (
          <EmailCards items={m.email_summaries} />
        )}
        {!m.pending && m.drive_files && m.drive_files.length > 0 && (
          <DriveCards items={m.drive_files} />
        )}
        {!m.pending && m.calendar_events && m.calendar_events.length > 0 && (
          <CalendarCards items={m.calendar_events} />
        )}
        {!isUser && !m.pending && (
          <div className="mt-3 flex items-center gap-2 text-[10.5px] text-muted-foreground">
            {m.model && (
              <span className="font-mono lowercase">
                {m.provider ? `${m.provider} · ` : ""}{m.model}
              </span>
            )}
            {m.image_backends && m.image_backends.length > 0 && (
              <span className="font-mono lowercase text-muted-foreground" title="image backend">
                {m.model ? "· " : ""}🖼 {m.image_backends.join(", ")}
              </span>
            )}
            {m.text && !m.id.startsWith("a-") && !m.id.startsWith("pending-") && <FeedbackButtons id={m.id} initial={m.feedback ?? null} />}
            {m.text && <ListenButton id={m.id} text={m.text} />}
          </div>
        )}
      </div>
    </div>
  )
}


/** Parse a `text/event-stream` body into JSON events (one per `data:` line). */
async function* readSSE(body: ReadableStream<Uint8Array>): AsyncGenerator<Record<string, unknown>> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buf = ""
  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      let sep: number
      while ((sep = buf.indexOf("\n\n")) >= 0) {
        const frame = buf.slice(0, sep)
        buf = buf.slice(sep + 2)
        for (const line of frame.split("\n")) {
          if (!line.startsWith("data:")) continue
          const raw = line.slice(5).trim()
          if (!raw) continue
          try { yield JSON.parse(raw) as Record<string, unknown> } catch { /* keep going */ }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

function fileToBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result).split(",")[1] || "")
    r.onerror = () => reject(r.error)
    r.readAsDataURL(f)
  })
}


function ListenButton({ id, text }: { id: string; text: string }) {
  const t = useT()
  const [active, setActive] = useState(speakingKey() === id)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => onSpeakingChange((k) => setActive(k === id)), [id])
  async function onClick() {
    setErr(null)
    if (active) { stopSpeaking(); return }
    setBusy(true)
    try { await speak(id, text) } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }
  return (
    <span className="inline-flex items-center gap-1">
      {err && <span className="text-destructive">{err}</span>}
      <button
        onClick={onClick}
        disabled={busy}
        title={active ? t("tts.stop") : t("tts.listen")}
        aria-label={active ? t("tts.stop") : t("tts.listen")}
        className={`flex h-6 w-6 items-center justify-center rounded-md transition hover:bg-foreground/[0.06] ${active ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
      >
        {busy ? <Spinner /> : active ? <SquareIcon className="h-3 w-3" fill="currentColor" /> : <Volume2Icon className="h-3.5 w-3.5" />}
      </button>
    </span>
  )
}


function FeedbackButtons({ id, initial }: { id: string; initial: "up" | "down" | null }) {
  const t = useT()
  const [rating, setRating] = useState<"up" | "down" | null>(initial)
  const [asking, setAsking] = useState(false)
  const [note, setNote] = useState("")
  const [busy, setBusy] = useState(false)
  async function send(r: "up" | "down" | null, n?: string) {
    setBusy(true)
    try {
      await api(`/api/dashboard/messages/${id}/feedback`, { method: "POST", body: { rating: r, note: n || null } })
      setRating(r)
    } catch { /* non-critical */ } finally { setBusy(false) }
  }
  return (
    <span className="ml-auto inline-flex items-center gap-1">
      <button
        onClick={() => void send(rating === "up" ? null : "up")}
        disabled={busy}
        title={t("fb.up")} aria-label={t("fb.up")} aria-pressed={rating === "up"}
        className={`flex h-6 w-6 items-center justify-center rounded-md transition hover:bg-foreground/[0.06] ${rating === "up" ? "text-[hsl(var(--ok))]" : "text-muted-foreground hover:text-foreground"}`}
      >
        <ThumbsUpIcon className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => { if (rating === "down") { void send(null) } else { setAsking(true) } }}
        disabled={busy}
        title={t("fb.down")} aria-label={t("fb.down")} aria-pressed={rating === "down"}
        className={`flex h-6 w-6 items-center justify-center rounded-md transition hover:bg-foreground/[0.06] ${rating === "down" ? "text-destructive" : "text-muted-foreground hover:text-foreground"}`}
      >
        <ThumbsDownIcon className="h-3.5 w-3.5" />
      </button>
      {asking && (
        <form
          className="ml-1 inline-flex items-center gap-1"
          onSubmit={(e) => { e.preventDefault(); void send("down", note.trim()); setAsking(false); setNote("") }}
        >
          <input
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("fb.why")}
            className="h-6 w-44 rounded-md border border-border bg-black/25 px-2 text-[11px] text-foreground outline-none"
            onKeyDown={(e) => { if (e.key === "Escape") { setAsking(false); void send("down") } }}
          />
          <button type="submit" className="rounded-md bg-primary/20 px-2 text-[11px] text-primary">OK</button>
        </form>
      )}
    </span>
  )
}


/** True when `heard` is mostly the same words as `spoken` (speaker → mic echo). */
function looksLikeEcho(heard: string, spoken: string): boolean {
  if (!spoken) return false
  const norm = (t: string) => t.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, " ").split(/\s+/).filter(Boolean)
  const a = norm(heard), b = new Set(norm(spoken))
  if (a.length < 3) return false
  const hits = a.filter((w) => b.has(w)).length
  return hits / a.length >= 0.75
}


function voiceStageState(
  s: "listening" | "speech" | "transcribing" | "paused" | "off",
  muted: boolean,
  talking: boolean,
  waiting: boolean,
): VoiceStageState {
  if (muted && !talking) return "muted"
  if (talking) return "speaking"
  if (s === "transcribing") return "transcribing"
  if (waiting) return "thinking"
  if (s === "speech") return "speech"
  if (s === "paused") return "paused"
  return "listening"
}
